import {
  List,
  TextField,
  FunctionField,
  EditButton,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  DateInput,
  SearchInput,
  CreateButton,
  useDataProvider,
  useNotify,
  useGetList,
  Button,
} from 'react-admin';
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { ImportButton } from '../components/ImportButton';
import { MeInvoiceImportButton } from '../components/MeInvoiceImportButton';
import { PageHeader } from '../components/PageHeader';
import { ResponsiveDataList } from '../components/ResponsiveDataList';
import { StatRow } from '../components/StatCards';
import { exportRows } from '../services/excelExport';
import { pick, str, num, normalizeDate } from '../utils/rows';
import { formatVnd } from '../utils/format';
import { formPageSx, pageShellSx, resourceListSx } from '../ui';
import type { VatInvoice } from '../types';

export const mapInvoiceRow = (row: Record<string, any>) => {
  const supplierName = pick(row, ['ncc', 'nhà cung cấp', 'người bán', 'supplier', 'tên']);
  const amount = num(pick(row, ['thành tiền', 'số tiền', 'tổng', 'amount', 'tiền']));
  if (!supplierName && amount == null) return null;
  return {
    invoiceNumber: str(pick(row, ['số hd', 'số hóa đơn', 'số ct', 'ký hiệu', 'invoice'])) || '(chưa có)',
    supplierName: str(supplierName),
    taxCode: str(pick(row, ['mst', 'tax'])),
    invoiceDate: normalizeDate(pick(row, ['ngày', 'date'])),
    amount: amount ?? 0,
  };
};

const ExportButton = () => {
  const dp = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);
    try {
      const { data } = await dp.getList<VatInvoice>('vatInvoices', {
        pagination: { page: 1, perPage: 10000 }, sort: { field: 'invoiceDate', order: 'DESC' }, filter: {},
      });
      exportRows(
        data.map((v) => ({
          'Số HĐ': v.invoiceNumber,
          'Nhà cung cấp': v.supplierName,
          MST: v.taxCode || '',
          Ngày: v.invoiceDate,
          'Số tiền (VND)': v.amount || 0,
        })),
        'hoa-don-vat',
        'HĐ VAT',
      );
      notify(`Đã xuất ${data.length} HĐ VAT ra Excel`, { type: 'success' });
    } catch {
      notify('Không thể xuất tệp Excel. Vui lòng thử lại.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  return <Button label="Xuất" onClick={onClick} disabled={loading} startIcon={<DownloadRoundedIcon />} />;
};

const InvoiceStats = () => {
  const { data = [] } = useGetList<VatInvoice>('vatInvoices', {
    pagination: { page: 1, perPage: 1000 }, sort: { field: 'invoiceDate', order: 'DESC' },
  });
  const total = data.length;
  const totalAmount = data.reduce((a, v) => a + (v.amount || 0), 0);
  const suppliers = new Set(data.map((v) => v.supplierName).filter(Boolean));
  const withTax = data.filter((v) => v.taxCode).length;
  const pct = total ? Math.round((withTax / total) * 100) : 0;
  return (
    <StatRow
      stats={[
        { label: 'Tổng số hóa đơn', value: total, sub: 'Hóa đơn đã lưu', icon: <ReceiptRoundedIcon />, color: '#0E7490' },
        { label: 'Tổng tiền', value: formatVnd(totalAmount), sub: 'Giá trị các hóa đơn', icon: <PaidRoundedIcon />, color: '#0FAF78' },
        { label: 'Nhà cung cấp', value: suppliers.size, sub: 'Nhà cung cấp khác nhau', icon: <StoreRoundedIcon />, color: '#2F80ED' },
        { label: 'Có mã số thuế', value: `${pct}%`, sub: `${withTax}/${total} hóa đơn`, icon: <FingerprintRoundedIcon />, color: '#F59E0B' },
      ]}
    />
  );
};

const invoiceFilters = [
  <SearchInput key="q" source="q" placeholder="Tìm theo số HĐ, nhà cung cấp, MST..." alwaysOn />,
  <DateInput key="invoiceDate" source="invoiceDate" label="Ngày hóa đơn" alwaysOn />,
];

export const VatInvoiceList = () => (
  <Box sx={pageShellSx}>
    <PageHeader
      title="Hóa đơn VAT"
      subtitle="Quản lý hóa đơn giá trị gia tăng dùng để đối soát với các đơn nợ."
      icon={<DescriptionRoundedIcon />}
      actions={
        <>
          <MeInvoiceImportButton />
          <ImportButton resource="vatInvoices" mapRow={mapInvoiceRow} label="Nhập HĐ VAT (Excel)" />
          <CreateButton label="Tạo mới" variant="contained" />
          <ExportButton />
        </>
      }
    />
    <InvoiceStats />
    <List
      sx={resourceListSx}
      actions={false}
      filters={invoiceFilters}
      perPage={25}
      sort={{ field: 'invoiceDate', order: 'DESC' }}
      title="Hóa đơn VAT"
      empty={false}
    >
      <ResponsiveDataList<VatInvoice>
        ariaLabel="Danh sách hóa đơn VAT"
        primaryText={(r) => r.supplierName || '(chưa có nhà cung cấp)'}
        secondaryText={(r) => `HĐ ${r.invoiceNumber || '—'} · ${r.invoiceDate || 'Chưa có ngày'}`}
        tertiaryText={(r) => `${formatVnd(r.amount)}${r.taxCode ? ` · MST ${r.taxCode}` : ''}`}
        leftAvatar={(r) => String(r.supplierName || '?').trim().charAt(0).toUpperCase() || '?'}
      >
        <TextField source="invoiceNumber" label="Số HĐ" />
        <TextField source="supplierName" label="Nhà cung cấp" />
        <TextField source="taxCode" label="MST" emptyText="—" />
        <TextField source="invoiceDate" label="Ngày" />
        <FunctionField
          label="Số tiền"
          textAlign="right"
          render={(r: VatInvoice) => <Typography variant="body2" sx={{ fontWeight: 800, textAlign: 'right' }}>{formatVnd(r.amount)}</Typography>}
        />
        <EditButton label="Sửa" />
      </ResponsiveDataList>
    </List>
  </Box>
);

const FormFields = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, width: '100%' }}>
    <TextInput source="invoiceNumber" label="Số hóa đơn" fullWidth />
    <DateInput source="invoiceDate" label="Ngày hóa đơn" fullWidth />
    <TextInput source="supplierName" label="Nhà cung cấp" fullWidth sx={{ gridColumn: { md: '1 / -1' } }} />
    <TextInput source="taxCode" label="MST" fullWidth />
    <NumberInput source="amount" label="Số tiền (VND)" fullWidth />
    <TextInput source="note" label="Ghi chú" fullWidth multiline minRows={4} sx={{ gridColumn: { md: '1 / -1' } }} />
  </Box>
);

export const VatInvoiceEdit = () => (
  <Box sx={pageShellSx}>
    <PageHeader title="Cập nhật hóa đơn VAT" subtitle="Điều chỉnh thông tin hóa đơn trước khi thực hiện đối soát." icon={<DescriptionRoundedIcon />} />
    <Edit title="Sửa HĐ VAT" sx={formPageSx}>
      <SimpleForm><FormFields /></SimpleForm>
    </Edit>
  </Box>
);

export const VatInvoiceCreate = () => (
  <Box sx={pageShellSx}>
    <PageHeader title="Thêm hóa đơn VAT" subtitle="Tạo hóa đơn VAT mới trong danh mục đối soát." icon={<DescriptionRoundedIcon />} />
    <Create title="Thêm HĐ VAT" sx={formPageSx}>
      <SimpleForm><FormFields /></SimpleForm>
    </Create>
  </Box>
);
